import IconMyDocuments from '@/assets/svg/icon-my-docs.svg?react';
import IconSettings from '@/assets/svg/icon-settings.svg?react';
import IconUser from '@/assets/svg/icon-user.svg?react';
import IconReportAnalytics from '@/assets/svg/nav-analytics.svg?react';
import IconSearch from '@/assets/svg/nav-search.svg?react';
import IconUploadFiles from '@/assets/svg/nav-upload-files.svg?react';

import { USER_ROLES, UserRoleType } from '@/constants/global-constants';

type RouteConfig = {
  path: string;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  title: string;
  pathsInclude?: string[];
  isNav?: boolean;
};

// Admin routes with organized structure
export const adminRoutes = {
  users: '/users',
  addUser: '/add-user',
  editUser: '/edit-user/:uuid',
  settings: '/settings',
  processes: {
    list: 'processes/list',
    add: 'processes/add',
    edit: 'processes/edit/:uuid',
    copy: 'processes/copy/:uuid',
  },
  indexes: 'indexes',
  fileValidation: 'file-validation',
  tags: 'tags',
  directories: 'directories',
} as const;

export const executorRoutes = {
  myDocuments: '/my-documents',
  uploadFiles: '/upload-files',
  uploadFilesById: '/upload-files/:uuid',
  reportAnalytics: '/report-analytics',
  search: '/file-search',
  fileSearchResult: '/file-search-result',
} as const;

export const controllerRoutes = {
  myDocuments: '/my-documents',
  uploadFiles: '/upload-files',
  uploadFilesById: '/upload-files/:uuid',
  reportAnalytics: '/report-analytics',
  search: '/file-search',
  fileSearchResult: '/file-search-result',
} as const;

export const externalSystemRoutes = {
  root: '/',
} as const;

export const privateRoutes = {
  [USER_ROLES.ADMIN.value]: adminRoutes,
  [USER_ROLES.EXECUTOR.value]: executorRoutes,
  [USER_ROLES.CONTROLLER.value]: controllerRoutes,
  [USER_ROLES.EXTERNAL_SYSTEM.value]: externalSystemRoutes,
} as const;

export const publicRoutes = {
  root: '/',
  login: '/login',
  noMatch: '*',
} as const;

// Helper function to create route config
const createRouteConfig = ({
  path,
  title,
  icon,
  isNav,
  pathsInclude = [],
}: RouteConfig): RouteConfig => ({
  path,
  title,
  ...(icon && { icon }),
  ...(isNav && { isNav }),
  ...{ pathsInclude },
});

// Helper function to create settings route
const createSettingsRoute = (path: string): RouteConfig =>
  createRouteConfig({
    path,
    title: 'Налаштування системи',
    icon: IconSettings,
  });

// Admin routes configuration
const adminRoutesConfig: Record<string, RouteConfig> = {
  [adminRoutes.users]: createRouteConfig({
    path: adminRoutes.users,
    title: 'Користувачі',
    icon: IconUser,
    isNav: true,
    pathsInclude: [
      adminRoutes.addUser,
      adminRoutes.editUser.replace('/:uuid', ''),
    ] as string[],
  }),
  [adminRoutes.addUser]: createRouteConfig({
    path: adminRoutes.addUser,
    title: 'Додати користувача',
    icon: IconUser,
  }),
  [adminRoutes.editUser]: createRouteConfig({
    path: adminRoutes.editUser,
    title: 'Редагувати користувача',
    icon: IconUser,
  }),
  [adminRoutes.settings]: createRouteConfig({
    path: `${adminRoutes.settings}/${adminRoutes.fileValidation}`,
    title: 'Налаштування системи',
    icon: IconSettings,
    isNav: true,
    pathsInclude: [
      adminRoutes.fileValidation,
      adminRoutes.tags,
      adminRoutes.indexes,
      adminRoutes.processes.add,
      adminRoutes.processes.list,
      adminRoutes.processes.edit.replace(':uuid', ''),
      adminRoutes.processes.copy.replace(':uuid', ''),
      adminRoutes.directories,
    ] as string[],
  }),
  [adminRoutes.processes.add]: createRouteConfig({
    path: `${adminRoutes.settings}/${adminRoutes.processes.add}`,
    title: 'Налаштування системи',
    icon: IconSettings,
  }),
  [adminRoutes.processes.list]: createRouteConfig({
    path: `${adminRoutes.settings}/${adminRoutes.processes.list}`,
    title: 'Налаштування системи',
    icon: IconSettings,
  }),
  [adminRoutes.processes.edit]: createRouteConfig({
    path: `${adminRoutes.settings}/${adminRoutes.processes.edit.replace(
      ':uuid',
      '',
    )}`,
    title: 'Налаштування системи',
    icon: IconSettings,
  }),
  [adminRoutes.processes.copy]: createRouteConfig({
    path: `${adminRoutes.settings}/${adminRoutes.processes.copy}`,
    title: 'Налаштування системи',
    icon: IconSettings,
  }),
};

// Generate settings sub-routes
const settingsSubRoutes = [
  adminRoutes.fileValidation,
  adminRoutes.tags,
  adminRoutes.indexes,
].reduce(
  (acc, route) => ({
    ...acc,
    [`${adminRoutes.settings}/${route}`]: createSettingsRoute(
      `${adminRoutes.settings}/${route}`,
    ),
  }),
  {},
);

const executorRoutesConfig: Record<string, RouteConfig> = {
  [executorRoutes.myDocuments]: createRouteConfig({
    path: executorRoutes.myDocuments,
    title: 'Мої документи',
    icon: IconMyDocuments,
    isNav: true,
  }),
  [executorRoutes.search]: createRouteConfig({
    path: executorRoutes.search,
    title: 'Пошук файлів',
    icon: IconSearch,
    isNav: true,
    pathsInclude: [executorRoutes.fileSearchResult],
  }),
  [executorRoutes.uploadFiles]: createRouteConfig({
    path: executorRoutes.uploadFiles,
    title: 'Завантаження файлів',
    icon: IconUploadFiles,
    isNav: true,
  }),
  [executorRoutes.reportAnalytics]: createRouteConfig({
    path: executorRoutes.reportAnalytics,
    title: 'Звітність та аналітика',
    icon: IconReportAnalytics,
    isNav: true,
  }),
};
const controllerRoutesConfig: Record<string, RouteConfig> = {
  [controllerRoutes.myDocuments]: createRouteConfig({
    path: controllerRoutes.myDocuments,
    title: 'Мої документи',
    icon: IconMyDocuments,
    isNav: true,
  }),
  [controllerRoutes.search]: createRouteConfig({
    path: controllerRoutes.search,
    title: 'Пошук файлів',
    icon: IconSearch,
    isNav: true,
    pathsInclude: [executorRoutes.fileSearchResult],
  }),
  [controllerRoutes.uploadFiles]: createRouteConfig({
    path: controllerRoutes.uploadFiles,
    title: 'Завантаження файлів',
    icon: IconUploadFiles,
    isNav: true,
  }),
  [controllerRoutes.reportAnalytics]: createRouteConfig({
    path: controllerRoutes.reportAnalytics,
    title: 'Звітність та аналітика',
    icon: IconReportAnalytics,
    isNav: true,
  }),
};

export const ROUTES: { [K in UserRoleType]: Record<string, RouteConfig> } = {
  [USER_ROLES.ADMIN.value]: {
    ...adminRoutesConfig,
    ...settingsSubRoutes,
  },
  [USER_ROLES.EXECUTOR.value]: {
    ...executorRoutesConfig,
  },
  [USER_ROLES.CONTROLLER.value]: {
    ...controllerRoutesConfig,
  },
  [USER_ROLES.EXTERNAL_SYSTEM.value]: {},
} as const;

export type RouteNameValues =
  (typeof privateRoutes)[keyof typeof privateRoutes] &
    (typeof publicRoutes)[keyof typeof publicRoutes];

export const getPageTitle = (pathname: string, role: UserRoleType): string => {
  const route = Object.values(ROUTES[role]).find((r) => {
    const routePath = r.path.replace(/\/:uuid/, '');
    return pathname.includes(routePath);
  });
  return route?.title || '';
};
